import cx from 'classnames';
import {isEmpty, map} from 'lodash';
import {useTranslation} from 'react-i18next';

import {
    Accordion, AccordionDetails, AccordionShortDetails, AccordionTitle, CopyText,
    Icon, DataWrapper
} from 'components';
import {TRANSLATION_KEYS} from 'locale/translation-keys';
import {getAssignmentName} from 'services';

import {
    ActivityInfo, FilesInfo, ParticipantsInfo, SessionResultInfo, StartDateInfo, EndDateInfo, DurationInfo
} from '../SessionHistoryCell';
import {SessionHistoryViewProps} from '../types';

import {GridRow} from './GridRow';

const T_KEYS = TRANSLATION_KEYS.USER.ACTIVITY.SESSION_HISTORY.SESSION_HISTORY_TABLE;

export const SessionHistoryGrid = ({
    sessions = [], users, groups, isLoading, className, downloadingFiles, textChatHistoryEnabled, avatarHistoryEnabled,
    agentChatbotHistoryEnabled,
    onOpenAuditModal, onDownload, onPreview, onOpenNotesModal, onSelectFilePipeline, onOpenActivityMessagesModal, downloadFileDisabled
}: SessionHistoryViewProps) => {
    const {t} = useTranslation();

    return (
        <DataWrapper isLoading={isLoading} noData={isEmpty(sessions)}>
            <div className={cx(className, 'tw-grid tw-min-h-8')}>
                {map(sessions, (session) => {
                    const assignment = getAssignmentName(groups, session.assignment?.groupId, session.assignment?.subgroupId);
                    return (
                        <Accordion key={session.sessionId} className='tw-min-w-48 tw-mb-2 last:tw-mb-0'>
                            <AccordionTitle>
                                <div className='tw-flex tw-items-center tw-gap-1'>
                                    <div className='tw-ts-h5 tw-text-default-dimmed'>
                                        <Icon name='id' size='lg' />
                                    </div>
                                    <div className='tw-ts-h5-bold tw-flex-shrink tw-overflow-hidden tw-text-ellipsis'>
                                        {session.sessionId}
                                    </div>
                                    <div className='tw-ml-auto -sm:tw-mr-2'>
                                        <CopyText
                                            text={session.sessionId}
                                            iconOnly
                                            iconColor='default'
                                            copiedLabelOnly
                                        />
                                    </div>
                                </div>
                            </AccordionTitle>
                            <AccordionShortDetails className='tw-px-6 tw-flex tw-flex-col tw-gap-1'>
                                <ParticipantsInfo session={session} users={users} groups={groups} />
                                <div className='tw-flex tw-my-1'>
                                    <Icon name='calendar' size='md' className='tw-mr-2' />
                                    <StartDateInfo session={session} />
                                </div>
                                <SessionResultInfo session={session} onOpenNotesModal={onOpenNotesModal} />
                            </AccordionShortDetails>
                            <AccordionDetails className='sm:tw-px-6 tw-flex tw-flex-col tw-gap-1'>
                                <GridRow label={t(T_KEYS.COLUMNS.PARTICIPANTS)}>
                                    <ParticipantsInfo session={session} users={users} groups={groups} />
                                </GridRow>
                                <GridRow label={t(T_KEYS.COLUMNS.STARTED)}>
                                    <StartDateInfo session={session} />
                                </GridRow>
                                <GridRow label={t(T_KEYS.COLUMNS.ENDED)}>
                                    <EndDateInfo session={session} />
                                </GridRow>
                                <GridRow label={t(T_KEYS.COLUMNS.DURATION)}>
                                    <DurationInfo session={session} />
                                </GridRow>
                                {session?.result &&
                                    <GridRow label={t(T_KEYS.COLUMNS.RESULT)}>
                                        <SessionResultInfo session={session} onOpenNotesModal={onOpenNotesModal} />
                                    </GridRow>}
                                {assignment &&
                                    <GridRow label={t(T_KEYS.COLUMNS.ASSIGNMENT)}>
                                        {assignment}
                                    </GridRow>}
                                {!isEmpty(session.activities) &&
                                    <GridRow label={t(T_KEYS.COLUMNS.ACTIVITY)}>
                                        <div className='tw-flex tw-flex-wrap tw-gap-1'>
                                            <ActivityInfo
                                                session={session}
                                                avatarHistoryEnabled={avatarHistoryEnabled}
                                                agentChatbotHistoryEnabled={agentChatbotHistoryEnabled}
                                                textChatHistoryEnabled={textChatHistoryEnabled}
                                                onOpenActivityMessagesModal={onOpenActivityMessagesModal}
                                            />
                                        </div>
                                    </GridRow>}
                                {!isEmpty(session.files) &&
                                    <GridRow label={t(T_KEYS.COLUMNS.FILES)}>
                                        <FilesInfo
                                            session={session}
                                            downloadingFiles={downloadingFiles}
                                            downloadFileDisabled={downloadFileDisabled}
                                            onDownload={onDownload}
                                            onPreview={onPreview}
                                            onPipelineSelect={onSelectFilePipeline}
                                        />
                                    </GridRow>}
                                {session.hasAudit &&
                                    <GridRow label={t(T_KEYS.COLUMNS.AUDIT)}>
                                        <Icon
                                            name='file-text'
                                            size='sm'
                                            className='tw-cursor-pointer'
                                            onClick={() => onOpenAuditModal(session.sessionId)}
                                        />
                                    </GridRow>}
                            </AccordionDetails>
                        </Accordion>
                    );
                })}
            </div>
        </DataWrapper>
    );
};
